"use client";

import {
  AlertTriangle,
  Ban,
  BookOpen,
  Calendar,
  FileText,
  MessageSquare,
  Settings,
} from "lucide-react";
import { Icon } from "@iconify-icon/react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { motion } from "motion/react";

import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import useWorkspaceInformationStore from "@/features/workspaces/store/useWorkspaceInformationStore.tsx";
import { STUDENT_WORKSPACE_PATHS } from "@/routes/paths.ts";
import { CoachMarker } from "@/components/CoachMarker.tsx";

export const StudentsWorkspace = () => {
  const { t } = useTranslation(["workspaces", "common", "help"]);
  const { data: workspaceInfo, members } = useWorkspaceInformationStore();
  const { workspaceId } = useParams();

  if (!workspaceInfo) {
    return null;
  }

  const isWorkspaceInactive = workspaceInfo.has_user_left_workspace;
  const isWorkspaceBlocked = workspaceInfo.is_workspace_blocked_for_user;
  const tutors = members ? members.filter((member) => member.type === "tutor") : [];

  // Get title text color based on user state
  const getTitleColor = () => {
    if (isWorkspaceBlocked) {
      return "text-red-300";
    }
    if (isWorkspaceInactive) {
      return "text-amber-500";
    }

    return "text-accent-foreground";
  };

  // Navigation cards data based on actual available pages
  const navigationCards = [
    {
      title: t("workspaces:schedule", "Schedule"),
      description: t("workspaces:scheduleDescription", "Your upcoming lessons and events"),
      icon: <Calendar className="h-6 w-6 text-blue-400" />,
      path: STUDENT_WORKSPACE_PATHS(workspaceId || "").SCHEDULE,
      bgGradient: "from-blue-500/10 to-purple-600/10",
      iconBg: "bg-blue-500/20",
      disabled: false,
    },
    {
      title: t("workspaces:lessons", "Lessons"),
      description: t("workspaces:lessonsDescription", "View your lesson history"),
      icon: <BookOpen className="h-6 w-6 text-green-400" />,
      path: STUDENT_WORKSPACE_PATHS(workspaceId || "").LESSONS,
      bgGradient: "from-green-500/10 to-emerald-600/10",
      iconBg: "bg-green-500/20",
      disabled: false,
    },
    {
      title: t("workspaces:conversation", "Conversation"),
      description: t("workspaces:conversationDescription", "Chat with your tutors"),
      icon: <MessageSquare className="h-6 w-6 text-purple-400" />,
      path: `${STUDENT_WORKSPACE_PATHS(workspaceId || "").CHAT}/${workspaceInfo.main_conversation_id}`,
      bgGradient: "from-purple-500/10 to-violet-600/10",
      iconBg: "bg-purple-500/20",
      disabled: false,
    },
    {
      title: t("common:materials", "Materials"),
      description: t("workspaces:materialsDescription", "Learning materials for your subjects"),
      icon: <FileText className="h-6 w-6 text-orange-400" />,
      path: STUDENT_WORKSPACE_PATHS(workspaceId || "").MATERIALS,
      bgGradient: "from-orange-500/10 to-red-600/10",
      iconBg: "bg-orange-500/20",
      disabled: true,
      soon: true,
    },
    {
      title: t("workspaces:homeworks", "Homeworks"),
      description: t("workspaces:homeworksDescription", "Your assignments and tasks"),
      icon: (
        <Icon
          className="text-pink-400"
          height={"1.5rem"}
          icon="mdi:clipboard-text"
          width={"1.5rem"}
        />
      ),
      path: STUDENT_WORKSPACE_PATHS(workspaceId || "").HOMEWORKS,
      bgGradient: "from-pink-500/10 to-rose-600/10",
      iconBg: "bg-pink-500/20",
      disabled: true,
      soon: true,
    },
    {
      title: t("common:settings", "Settings"),
      description: t("workspaces:settingsDescription", "Workspace preferences"),
      icon: <Settings className="h-6 w-6 text-gray-400" />,
      path: STUDENT_WORKSPACE_PATHS(workspaceId || "").SETTINGS,
      bgGradient: "from-gray-500/10 to-slate-600/10",
      iconBg: "bg-gray-500/20",
      disabled: false,
    },
  ];

  return (
    <ScrollArea className="h-[calc(100dvh_-_4rem)]">
      <div className="mx-auto max-w-6xl p-6">
        {/* Main Content Grid */}
        <div className="space-y-6">
          {/* Header Section */}
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4 }}
          >
            <CoachMarker
              description={t("help:coachMarkers.studentWorkspace.header.description" as any)}
              id="student-workspace-header"
              placement="bottom"
              title={t("help:coachMarkers.studentWorkspace.header.title" as any)}
            >
              <Card className="bg-side-accent/50 relative overflow-hidden rounded-2xl border-none p-8 shadow-2xl">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5">
                  <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br from-blue-400 to-purple-600" />
                  <div className="absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tr from-green-400 to-blue-500" />
                </div>

                <div className="relative">
                  {/* Workspace Header Info */}
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    {/* Workspace Identity */}
                    <div className="lg:col-span-2">
                      <div className="mb-4 flex items-center gap-6">
                        <motion.div
                          animate={{ scale: 1 }}
                          className="relative"
                          initial={{ scale: 0.8 }}
                          transition={{ duration: 0.3, delay: 0.2 }}
                        >
                          {/* Image backdrop only behind avatar */}
                          <div className="absolute inset-0 overflow-hidden rounded-full opacity-20">
                            <img
                              alt={workspaceInfo.name}
                              className="h-full w-full object-cover blur-2xl"
                              src={workspaceInfo.logo || "/placeholder.svg"}
                            />
                          </div>
                          <Avatar className="relative h-20 w-20 shadow-xl">
                            <AvatarImage
                              alt={workspaceInfo.name}
                              src={workspaceInfo.logo || "/placeholder.svg"}
                            />
                            <AvatarFallback className="bg-muted/20 text-2xl font-bold">
                              {workspaceInfo.name.substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                        </motion.div>
                        <div className="flex-1">
                          <h1 className={`text-3xl font-bold md:text-4xl ${getTitleColor()}`}>
                            {workspaceInfo.name}
                          </h1>

                          {/* Alert Section under title */}
                          {(isWorkspaceInactive || isWorkspaceBlocked) && (
                            <div className="mt-3">
                              {isWorkspaceInactive && (
                                <div className="flex items-center gap-2 text-amber-500">
                                  <AlertTriangle className="h-4 w-4" />
                                  <span className="text-sm font-medium">
                                    {t("workspaces:workspaceInactive.title", "Workspace Inactive")}
                                  </span>
                                </div>
                              )}
                              {isWorkspaceBlocked && (
                                <div className="flex items-center gap-2 text-red-300">
                                  <Ban className="h-4 w-4" />
                                  <span className="text-sm font-medium">
                                    {t("workspaces:workspaceBlocked.title", "Access Blocked")}
                                  </span>
                                  {workspaceInfo.blocked_reason && (
                                    <span className="text-xs">
                                      - {workspaceInfo.blocked_reason}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className="text-muted-foreground text-sm">
                              {t(
                                `workspaces:workspaceType.${workspaceInfo.workspace_type}`,
                                workspaceInfo.workspace_type,
                              )}
                            </span>
                            <span className="text-muted-foreground">•</span>
                            <span className="text-muted-foreground text-sm">
                              {t(
                                `common:roles.${workspaceInfo.user_workspace_role}`,
                                workspaceInfo.user_workspace_role,
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Workspace Stats */}
                    <div className="bg-muted/20 rounded-xl p-4 backdrop-blur-sm">
                      <h3 className="text-accent-foreground mb-3 text-sm font-semibold tracking-wide uppercase">
                        {t("workspaces:workspaceInfo", "Workspace Info")}
                      </h3>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground text-xs">
                            {t("workspaces:tutors", "Tutors")}
                          </span>
                          <span className="text-accent-foreground text-sm font-medium">
                            {tutors.filter((t) => !t.left_at && !t.blocked_at).length}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground text-xs">
                            {t("workspaces:totalMembers", "Total Members")}
                          </span>
                          <span className="text-accent-foreground text-sm font-medium">
                            {members?.length || 0}
                          </span>
                        </div>
                        {workspaceInfo.subjects && workspaceInfo.subjects.length > 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground text-xs">
                              {t("workspaces:subjects", "Subjects")}
                            </span>
                            <span className="text-accent-foreground text-sm font-medium">
                              {workspaceInfo.subjects.length}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </CoachMarker>
          </motion.div>

          {/* Navigation Cards Grid */}
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <CoachMarker
              description={t("help:coachMarkers.studentWorkspace.navigation.description" as any)}
              id="student-workspace-navigation-cards"
              placement="bottom"
              title={t("help:coachMarkers.studentWorkspace.navigation.title" as any)}
            >
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {navigationCards.map((card) => (
                  <Card
                    key={card.title}
                    className={`bg-muted/20 text-accent-foreground group relative overflow-hidden rounded-2xl border-none p-6 shadow-lg transition-all duration-300 ${
                      card.disabled
                        ? "cursor-not-allowed opacity-60"
                        : "hover:scale-[1.02] hover:shadow-xl"
                    }`}
                  >
                    {card.disabled ? (
                      <div className="block h-full">
                        {/* Background Gradient */}
                        <div
                          className={`absolute inset-0 bg-gradient-to-br ${card.bgGradient} opacity-0 transition-opacity duration-300 group-hover:opacity-100`}
                        />

                        <div className="relative">
                          <div className="mb-4 flex items-center justify-between">
                            <div
                              className={`flex h-12 w-12 items-center justify-center rounded-xl ${card.iconBg}`}
                            >
                              {card.icon}
                            </div>
                            {card.soon && (
                              <Badge className="text-xs" variant="secondary">
                                {t("common:soon", "Soon")}
                              </Badge>
                            )}
                          </div>

                          <h3 className="mb-2 text-lg font-bold">{card.title}</h3>
                          <p className="text-muted-foreground text-sm">{card.description}</p>
                        </div>
                      </div>
                    ) : (
                      <Link className="block h-full" to={card.path}>
                        {/* Background Gradient */}
                        <div
                          className={`absolute inset-0 bg-gradient-to-br ${card.bgGradient} opacity-0 transition-opacity duration-300 group-hover:opacity-100`}
                        />

                        <div className="relative">
                          <div className="mb-4 flex items-center justify-between">
                            <div
                              className={`flex h-12 w-12 items-center justify-center rounded-xl ${card.iconBg}`}
                            >
                              {card.icon}
                            </div>
                            <Icon
                              className="text-muted-foreground h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"
                              icon="mdi:arrow-right"
                            />
                          </div>

                          <h3 className="mb-2 text-lg font-bold">{card.title}</h3>
                          <p className="text-muted-foreground text-sm">{card.description}</p>
                        </div>
                      </Link>
                    )}
                  </Card>
                ))}
              </div>
            </CoachMarker>
          </motion.div>

          {/* Tutors Section */}
          {tutors.length > 0 && !isWorkspaceInactive && !isWorkspaceBlocked && (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <Card className="bg-foreground/50 rounded-2xl border-none p-6 shadow-lg">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-accent-foreground flex items-center gap-3 text-lg font-semibold">
                    <div className="bg-muted/20 flex size-10 items-center justify-center rounded-xl">
                      <Icon className="text-accent-foreground h-5 w-5" icon="mdi:account-tie" />
                    </div>
                    {t("workspaces:tutors", "Tutors")} (
                    {tutors.filter((t) => !t.left_at && !t.blocked_at).length})
                  </h3>
                </div>

                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {tutors
                    .filter((tutor) => !tutor.left_at && !tutor.blocked_at)
                    .slice(0, 6)
                    .map((tutor) => (
                      <motion.div
                        key={tutor.id}
                        animate={{ opacity: 1, scale: 1 }}
                        className="group bg-muted/50 hover:bg-muted/70 relative overflow-hidden rounded-lg p-3 transition-all duration-300"
                        initial={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                      >
                        {/* Background blur effect */}
                        <div className="absolute inset-0 top-1/2 -left-0 h-24 w-24 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full opacity-25">
                          <img
                            alt={tutor.full_name}
                            className="h-full w-full object-cover blur-2xl"
                            src={tutor.avatar_path || "/placeholder.svg"}
                          />
                        </div>

                        <div className="relative flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarImage
                              alt={tutor.full_name}
                              src={tutor.avatar_path || "/placeholder.svg"}
                            />
                            <AvatarFallback>
                              {tutor.full_name
                                .split(" ")
                                .map((n: string) => n[0])
                                .join("")}
                            </AvatarFallback>
                          </Avatar>

                          <div className="min-w-0 flex-1">
                            <h4 className="text-accent-foreground truncate text-sm font-medium">
                              {tutor.full_name}
                            </h4>
                            <p className="text-muted-foreground text-xs">
                              {t("common:roles.tutor", "Tutor")}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                </div>

                {tutors.filter((t) => !t.left_at && !t.blocked_at).length > 6 && (
                  <div className="mt-4 text-center">
                    <Button asChild className="h-8 gap-2 px-4 text-xs" size="sm" variant="outline">
                      <Link to={STUDENT_WORKSPACE_PATHS(workspaceId || "").TUTORS}>
                        {t("workspaces:viewMoreTutors", "View {{count}} more tutors", {
                          count: tutors.filter((t) => !t.left_at && !t.blocked_at).length - 6,
                        })}
                      </Link>
                    </Button>
                  </div>
                )}
              </Card>
            </motion.div>
          )}
        </div>
      </div>
    </ScrollArea>
  );
};
